import { useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Trash2, Save, FileText } from "lucide-react";
import { useMutation, useQueryClient, useQuery } from "@tanstack/react-query";
import { base44 } from "@/api/base44Client";
import { Progress } from "@/components/ui/progress";
import { cn } from "@/lib/utils";

export default function ScorecardSection({ candidate, candidateId }) {
  const [scorecard, setScorecard] = useState(candidate.scorecard || []);
  const [isEditing, setIsEditing] = useState(false);
  const [showTemplateSelector, setShowTemplateSelector] = useState(false);
  const queryClient = useQueryClient();

  const { data: templates = [] } = useQuery({
    queryKey: ["scorecard-templates"],
    queryFn: () => base44.entities.ScorecardTemplate.filter({ is_active: true }),
  });

  const updateCandidateMutation = useMutation({
    mutationFn: ({ id, data }) => base44.entities.AcquisitionCandidate.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["acquisition-candidate"] });
      setIsEditing(false);
    },
  });

  const addScorecardItem = () => {
    setScorecard([...scorecard, { category: "", score: 0, weight: 1, notes: "" }]);
    setIsEditing(true);
  };

  const removeScorecardItem = (index) => {
    setScorecard(scorecard.filter((_, i) => i !== index));
  };

  const updateScorecardItem = (index, field, value) => {
    const updated = [...scorecard];
    updated[index] = { ...updated[index], [field]: value };
    setScorecard(updated);
  };

  const calculateOverallScore = () => {
    if (scorecard.length === 0) return 0;
    const totalWeight = scorecard.reduce((sum, item) => sum + (item.weight || 0), 0);
    if (totalWeight === 0) return 0;
    const weightedSum = scorecard.reduce((sum, item) => sum + (item.score || 0) * (item.weight || 0), 0);
    return Math.round(weightedSum / totalWeight);
  };

  const applyTemplate = (templateId) => {
    const template = templates.find(t => t.id === templateId);
    if (template) {
      const newScorecard = template.categories.map(cat => ({
        category: cat.category,
        score: 0,
        weight: cat.weight,
        notes: cat.description || "",
      }));
      setScorecard(newScorecard);
      setIsEditing(true);
      setShowTemplateSelector(false);
    }
  };

  const handleSave = () => {
    const overallScore = calculateOverallScore();
    updateCandidateMutation.mutate({
      id: candidateId,
      data: { scorecard, overall_score: overallScore },
    });
  };

  const overallScore = calculateOverallScore();

  const getScoreColor = (score) => {
    if (score >= 80) return "text-emerald-600";
    if (score >= 60) return "text-amber-600";
    return "text-rose-600";
  };

  return (
    <div className="space-y-6">
      <Card className="p-6 bg-gradient-to-br from-lime-100 to-lime-100 border-0 shadow-sm">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-slate-900">Totalskår</h3>
          <div className="flex items-center gap-2">
            <span className={cn("text-4xl font-bold", getScoreColor(overallScore))}>
              {overallScore}
            </span>
            <span className="text-slate-500">/100</span>
          </div>
        </div>
        <Progress value={overallScore} className="h-3" />
      </Card>

      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold text-slate-900">Evalueringskriterier</h3>
        <div className="flex gap-2">
          {isEditing && (
            <Button onClick={handleSave} disabled={updateCandidateMutation.isPending}>
              <Save className="w-4 h-4 mr-2" />
              Lagre
            </Button>
          )}
          {templates.length > 0 && (
            <Button onClick={() => setShowTemplateSelector(!showTemplateSelector)} variant="outline">
              <FileText className="w-4 h-4 mr-2" />
              Bruk mal
            </Button>
          )}
          <Button onClick={addScorecardItem} variant="outline">
            <Plus className="w-4 h-4 mr-2" />
            Legg til Kriterie
          </Button>
        </div>
      </div>

      {showTemplateSelector && (
        <Card className="p-4 bg-white border-0 shadow-sm">
          <div className="space-y-3">
            <Label>Velg scorecard-mal</Label>
            <Select onValueChange={applyTemplate}>
              <SelectTrigger>
                <SelectValue placeholder="Velg en mal..." />
              </SelectTrigger>
              <SelectContent>
                {templates.map((template) => (
                  <SelectItem key={template.id} value={template.id}>
                    {template.name} ({template.categories.length} kategorier)
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-sm text-slate-500">
              Dette vil erstatte eksisterende scorecard med kategorier fra malen
            </p>
          </div>
        </Card>
      )}

      {scorecard.length === 0 ? (
        <Card className="p-12 text-center bg-white border-0 shadow-sm">
          <p className="text-slate-500 mb-4">Ingen evalueringskriterier ennå</p>
          <Button onClick={addScorecardItem} variant="outline">
            <Plus className="w-4 h-4 mr-2" />
            Legg til Første Kriterie
          </Button>
        </Card>
      ) : (
        <div className="space-y-4">
          {scorecard.map((item, index) => (
            <Card key={index} className="p-6 bg-white border-0 shadow-sm">
              {isEditing ? (
                <div className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="space-y-2">
                      <Label>Kategori</Label>
                      <Input
                        value={item.category}
                        onChange={(e) => updateScorecardItem(index, "category", e.target.value)}
                        placeholder="f.eks. Markedsposisjon"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Poeng (0-100)</Label>
                      <Input
                        type="number"
                        min="0"
                        max="100"
                        value={item.score}
                        onChange={(e) => updateScorecardItem(index, "score", parseFloat(e.target.value) || 0)}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Vekt (1-5)</Label>
                      <Input
                        type="number"
                        min="1"
                        max="5"
                        value={item.weight}
                        onChange={(e) => updateScorecardItem(index, "weight", parseFloat(e.target.value) || 1)}
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label>Notater</Label>
                    <Textarea
                      value={item.notes}
                      onChange={(e) => updateScorecardItem(index, "notes", e.target.value)}
                      rows={2}
                      placeholder="Legg til kommentarer..."
                    />
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => removeScorecardItem(index)}
                    className="text-rose-600 hover:text-rose-700"
                  >
                    <Trash2 className="w-4 h-4 mr-2" />
                    Fjern
                  </Button>
                </div>
              ) : (
                <div>
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-3">
                      <h4 className="font-semibold text-slate-900">{item.category}</h4>
                      <span className="text-xs text-slate-500 bg-slate-100 px-2 py-1 rounded">
                        Vekt: {item.weight}
                      </span>
                    </div>
                    <span className={cn("text-2xl font-bold", getScoreColor(item.score))}>
                      {item.score}
                    </span>
                  </div>
                  <Progress value={item.score} className="h-2 mb-3" />
                  {item.notes && (
                    <p className="text-sm text-slate-600">{item.notes}</p>
                  )}
                </div>
              )}
            </Card>
          ))}
        </div>
      )}

      {!isEditing && scorecard.length > 0 && (
        <Button onClick={() => setIsEditing(true)} variant="outline" className="w-full">
          Rediger Scorecard
        </Button>
      )}
    </div>
  );
}